import { assertNever } from "@msys/common";
import { Box, BoxProps, Chip, ChipProps, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React from "react";
import { notSetSymbol } from "../doc-items/helpers.js";
import { Props2AllFragment } from "../doc-items/properties.generated.js";

interface Props {
  props: Props2AllFragment[];
  wrap?: boolean;
  boxProps?: BoxProps;
  highlightedKeys?: string[];
}

export const PropertyValueChips = ({
  props,
  wrap = true,
  boxProps,
  highlightedKeys,
}: Props) => {
  return (
    <Box
      {...boxProps}
      sx={[
        {
          display: "flex",
          flexDirection: "row",
          alignItems: "baseline",
          flexWrap: wrap ? "wrap" : "nowrap",
          ml: -0.5,
          mt: -0.25,
        },
        ...(Array.isArray(boxProps?.sx) ? boxProps?.sx : [boxProps?.sx]),
      ]}
    >
      {props.map(prop => (
        <PropertyValueChip
          key={prop.key}
          prop={prop}
          sx={{ ml: 0.5, mt: 0.25 }}
          clickable={false}
          variant={highlightedKeys?.includes(prop.key) ? "filled" : "outlined"}
        />
      ))}
    </Box>
  );
};

export function PropertyValueChip({
  prop,
  ...chipProps
}: { prop: Props2AllFragment } & ChipProps) {
  const { t } = useTranslate(["Global"]);

  const value = React.useMemo(() => {
    switch (prop.__typename) {
      case "Props2Bool": {
        return isNil(prop.valueBool)
          ? notSetSymbol
          : prop.valueBool === true
            ? t("Yes", { ns: "Global" })
            : t("No", { ns: "Global" });
      }
      case "Props2BoolComputed": {
        return isNil(prop.valueBoolComputed)
          ? notSetSymbol
          : prop.valueBoolComputed === true
            ? t("Yes", { ns: "Global" })
            : t("No", { ns: "Global" });
      }
      case "Props2Number": {
        return isNil(prop.valueNumber)
          ? notSetSymbol
          : [prop.valueNumber, prop.unit].filter(Boolean).join("");
      }
      case "Props2NumberArray": {
        return isNil(prop.valueNumberArray) || !prop.valueNumberArray.length
          ? notSetSymbol
          : prop.valueNumberArray
              .map(v => [v.toString(), prop.unit].filter(Boolean).join(""))
              .join(", ");
      }
      case "Props2NumberArrayComputed": {
        return isNil(prop.valueNumberArrayComputed) ||
          !prop.valueNumberArrayComputed
          ? notSetSymbol
          : prop.valueNumberArrayComputed
              .map(v => [v.toString(), prop.unit].filter(Boolean).join(""))
              .join(", ");
      }
      case "Props2NumberComputed": {
        return isNil(prop.valueNumberComputed)
          ? notSetSymbol
          : [prop.valueNumberComputed, prop.unit].filter(Boolean).join("");
      }
      case "Props2Text": {
        return isNil(prop.valueText) ? notSetSymbol : prop.valueText;
      }
      case "Props2TextComputed": {
        return isNil(prop.valueTextComputed)
          ? notSetSymbol
          : prop.valueTextComputed;
      }
      case "Props2TextArray": {
        return isNil(prop.valueTextArray) || !prop.valueTextArray.length
          ? notSetSymbol
          : prop.valueTextArray.join(", ");
      }
      case "Props2TextArrayComputed": {
        return isNil(prop.valueTextArrayComputed) ||
          !prop.valueTextArrayComputed.length
          ? notSetSymbol
          : prop.valueTextArrayComputed.join(",");
      }
      default:
        assertNever(prop);
    }
  }, [prop, t]);

  return (
    <Tooltip title={prop.label} key={prop.key}>
      <Chip
        size={"small"}
        variant="outlined"
        color="primary"
        {...chipProps}
        label={
          prop.__typename === "Props2Text" ||
          prop.__typename === "Props2TextComputed" ||
          prop.__typename === "Props2TextArray" ||
          prop.__typename === "Props2TextArrayComputed"
            ? value
            : `${prop.label}: ${value}`
        }
      />
    </Tooltip>
  );
}
